/**
 * Local solo statistics — games started/finished, lines cleared, pieces
 * placed, time played, one lifetime total per mode id — in localStorage.
 *
 * Best times are `personal-bests-io.ts`'s job, keyed by the full mode
 * *configuration*; this module tracks the coarser, mode-wide totals
 * that make sense summed across every configuration a player has tried
 * (`docs/decisions/0037` says why the two are kept apart rather than folded
 * into one store).
 *
 * A plain module, not a `.svelte.ts` one — same reason `personal-bests-io.ts`
 * gives for itself: no reactive state here, only a read/update/write cycle a
 * caller drives, so this is unit-testable without the Svelte compiler.
 */
import { safeLocalStorage } from './settings-io.js';

export const SOLO_STATS_STORAGE_KEY = 'interlock:solo-stats:v1';

export interface ModeStats {
  readonly gamesStarted: number;
  readonly gamesFinished: number;
  readonly linesCleared: number;
  readonly piecesPlaced: number;
  readonly timePlayedMs: number;
}

export type SoloStatsStore = Record<string, ModeStats>;

const EMPTY_MODE_STATS: ModeStats = {
  gamesStarted: 0,
  gamesFinished: 0,
  linesCleared: 0,
  piecesPlaced: 0,
  timePlayedMs: 0,
};

function readStore(storage: Storage | undefined): SoloStatsStore {
  if (storage === undefined) return {};
  try {
    const raw = storage.getItem(SOLO_STATS_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as SoloStatsStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: SoloStatsStore, storage: Storage | undefined): void {
  if (storage === undefined) return;
  try {
    storage.setItem(SOLO_STATS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked (private browsing) — same policy as every
    // other local store in this codebase: stop persisting, don't interrupt play.
  }
}

/** One mode's totals, or all zeros — never `null`, since "never played" and "zero of everything" read identically here. */
export function loadModeStats(
  modeId: string,
  storage: Storage | undefined = safeLocalStorage(),
): ModeStats {
  return readStore(storage)[modeId] ?? EMPTY_MODE_STATS;
}

/** Every mode this device has ever played, for the stats screen and for the login-time push. */
export function loadAllModeStats(
  storage: Storage | undefined = safeLocalStorage(),
): SoloStatsStore {
  return readStore(storage);
}

/**
 * A run began. Called once, when a mode actually starts (`GameScreen.svelte`'s
 * `onMount`) — a run abandoned at the mode-select screen before Start was
 * pressed never reaches this, and a run quit mid-way still counts as
 * *started*, which is the distinction "started" vs. "finished" exists to
 * show.
 */
export function recordGameStart(
  modeId: string,
  storage: Storage | undefined = safeLocalStorage(),
): void {
  const store = readStore(storage);
  const current = store[modeId] ?? EMPTY_MODE_STATS;
  store[modeId] = { ...current, gamesStarted: current.gamesStarted + 1 };
  writeStore(store, storage);
}

/**
 * A run reached a real outcome — `mode.checkEnd` returned non-null, whether
 * that was a win, a loss or a timed "finished". A quit never calls this,
 * which is what makes "finished" a meaningful number next to "started".
 * Returns the mode's totals now on record, the same "hand back what now
 * stands" shape `recordPersonalBest` uses.
 */
export function recordGameFinish(
  modeId: string,
  delta: { readonly lines: number; readonly pieces: number; readonly timeMs: number },
  storage: Storage | undefined = safeLocalStorage(),
): ModeStats {
  const store = readStore(storage);
  const current = store[modeId] ?? EMPTY_MODE_STATS;
  const next: ModeStats = {
    gamesStarted: current.gamesStarted,
    gamesFinished: current.gamesFinished + 1,
    linesCleared: current.linesCleared + Math.max(0, delta.lines),
    piecesPlaced: current.piecesPlaced + Math.max(0, delta.pieces),
    timePlayedMs: current.timePlayedMs + Math.max(0, delta.timeMs),
  };
  store[modeId] = next;
  writeStore(store, storage);
  return next;
}

/** True when a mode has no recorded history at all — the em-dash case a stats screen should show instead of a row of zeros. */
export function hasAnyStats(stats: ModeStats): boolean {
  return stats.gamesStarted > 0;
}

/**
 * Take a merged set back down from the account, mirroring
 * `personal-bests-io.ts#mergePersonalBests`'s shape: keep the larger of each
 * field per mode id, the same elementwise-max rule the server applies
 * (`apps/server/src/accounts/model.ts#betterSoloStats`) — sound because a
 * device always reports its own lifetime-so-far totals, never a delta, so
 * neither side can double-count a game the other already knows about.
 */
export function mergeSoloStats(
  incoming: Iterable<ModeStats & { readonly modeId: string }>,
  storage: Storage | undefined = safeLocalStorage(),
): SoloStatsStore {
  const store = readStore(storage);
  let changed = false;
  for (const entry of incoming) {
    const current = store[entry.modeId];
    const merged: ModeStats = {
      gamesStarted: Math.max(entry.gamesStarted, current?.gamesStarted ?? 0),
      gamesFinished: Math.max(entry.gamesFinished, current?.gamesFinished ?? 0),
      linesCleared: Math.max(entry.linesCleared, current?.linesCleared ?? 0),
      piecesPlaced: Math.max(entry.piecesPlaced, current?.piecesPlaced ?? 0),
      timePlayedMs: Math.max(entry.timePlayedMs, current?.timePlayedMs ?? 0),
    };
    if (current === undefined || !sameStats(merged, current)) {
      store[entry.modeId] = merged;
      changed = true;
    }
  }
  if (changed) writeStore(store, storage);
  return store;
}

function sameStats(a: ModeStats, b: ModeStats): boolean {
  return (
    a.gamesStarted === b.gamesStarted &&
    a.gamesFinished === b.gamesFinished &&
    a.linesCleared === b.linesCleared &&
    a.piecesPlaced === b.piecesPlaced &&
    a.timePlayedMs === b.timePlayedMs
  );
}
